
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/services/supabase/supabase';
import { AuthContextType } from './authTypes';
import { signOut, signIn, signUp, fetchUserProfile } from './authOperations';
import { clearAuthData } from './authUtils';
import { useToast } from "@/hooks/use-toast";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Set up the auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log("Auth state change event:", event);
        
        if (event === 'SIGNED_OUT') {
          // Clear state immediately on sign out
          clearAuthData();
          setUser(null);
          setSession(null);
          setIsFirstLogin(false);
          setIsLoading(false);
          return;
        }
        
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        // Use setTimeout to prevent deadlocks with Supabase auth
        if (newSession?.user) {
          setTimeout(async () => {
            const firstLogin = await fetchUserProfile(newSession.user.id);
            setIsFirstLogin(firstLogin);
          }, 0);
        } else {
          setIsFirstLogin(false);
        }
        
        setIsLoading(false);
      }
    );

    // Then check for an existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      // Use setTimeout to prevent deadlocks with Supabase auth
      if (currentSession?.user) {
        setTimeout(async () => {
          const firstLogin = await fetchUserProfile(currentSession.user.id);
          setIsFirstLogin(firstLogin);
        }, 0);
      }
      
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    user,
    session,
    isLoading,
    loading: isLoading, // Add loading property for backward compatibility
    isFirstLogin,
    signIn: (email: string, password: string) => signIn(email, password),
    signUp: (email: string, password: string, fullName: string) => signUp(email, password, fullName, toast),
    signOut: () => signOut(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
